/** @format */
/**
 * Granular consent preferences — the "manage / change your mind" surface.
 *
 * Spec §7 requires withdrawal to be AS EASY AS granting. That is why the
 * "Withdraw all consent" action is a first-class button inside this dialog,
 * one click, no confirmation maze, no email-support instruction: a UI where
 * opting in is one tap and opting out takes five is itself the violation.
 *
 * This component is mountable at ANY time (footer link → open), not only on
 * first visit. It is a controlled dialog: the parent owns `open`.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getConsent,
  setConsent,
  withdraw,
  defaultsForRegion,
} from "../../utils/consent";
import "./ConsentPreferences.css";

const CATEGORY_COPY = [
  {
    key: "necessary",
    title: "Strictly necessary",
    body:
      "Keeps you signed in, remembers your gym, secures forms and stores this very cookie choice. " +
      "The site cannot work without these, so they cannot be switched off.",
  },
  {
    key: "analytics",
    title: "Analytics",
    body:
      "Google Analytics 4, loaded through Google Tag Manager. Counts page views and which " +
      "features get used, so we can fix the parts people struggle with. Never used to advertise to you.",
  },
  {
    key: "marketing",
    title: "Marketing",
    body:
      "Meta (Facebook/Instagram) Pixel and Google Ads tags. Measures which ads brought you here and " +
      "lets Meta and Google show you WTF Go ads on their platforms. This is the cross-site tracking one.",
  },
];

/**
 * Reads the store fresh. FAILURE MODE: rendering from a value captured at
 * module load would show a stale grant after the user withdrew in another tab.
 */
function readSelection(region) {
  const stored = getConsent();
  if (stored) {
    return { analytics: stored.analytics === true, marketing: stored.marketing === true };
  }
  // NO DECISION YET. Every toggle starts OFF, in EVERY region.
  //
  // defaultsForRegion() is deliberately NOT used to seed this dialog. It encodes
  // what may happen WITHOUT interaction in an opt-out jurisdiction — a policy
  // question. What the dialog SHOWS is a different question: a pre-ticked box
  // asserts a choice the user has not made, which is a dark pattern and is
  // invalid consent under GDPR (and increasingly under US state law).
  //
  // Nothing is lost by starting OFF: isAllowed() already returns false until a
  // decision exists, so these values only ever seeded the UI. Pre-ticking them
  // bought no tracking and cost us the legality of the consent.
  void defaultsForRegion;   // kept imported: region policy is used elsewhere
  return { analytics: false, marketing: false };
}

export function ConsentPreferences({ open, onClose, region = null, returnFocusTo = null }) {
  const [selection, setSelection] = useState(() => readSelection(region));
  const dialogRef = useRef(null);
  const firstFocusRef = useRef(null);
  // Remembers whatever had focus when we opened, so we can hand it back even
  // when the caller passes no explicit opener ref.
  const openerRef = useRef(null);

  // Re-sync every time the dialog is opened. Without this, opening → closing →
  // withdrawing elsewhere → reopening would show the pre-withdrawal toggles.
  useEffect(() => {
    if (open) setSelection(readSelection(region));
  }, [open, region]);

  const close = useCallback(() => {
    if (typeof onClose === "function") onClose();
  }, [onClose]);

  // --- focus management -----------------------------------------------------
  useEffect(() => {
    if (!open) return undefined;
    openerRef.current =
      (returnFocusTo && returnFocusTo.current) ||
      (document.activeElement instanceof HTMLElement ? document.activeElement : null);
    firstFocusRef.current?.focus();
    return () => {
      // The opener may have been DESTROYED by the very action that closed this
      // dialog: saving from the banner records a decision, the banner unmounts,
      // and its "Manage" button no longer exists. Focusing a detached node
      // silently drops focus to <body> — the keyboard user loses their place at
      // the exact moment they finished a privacy choice. So fall back to a real
      // landmark that is still in the document.
      const preferred = (returnFocusTo && returnFocusTo.current) || openerRef.current;
      const stillInDocument = preferred && document.contains(preferred);
      let target = stillInDocument ? preferred : null;
      if (!target) {
        target =
          document.querySelector("main") ||
          document.getElementById("root") ||
          document.body;
        // A landmark is not focusable by default; make it programmatically
        // focusable without adding it to the tab order.
        if (target && target !== document.body && !target.hasAttribute("tabindex")) {
          target.setAttribute("tabindex", "-1");
        }
      }
      // Focus must go back to the opener; leaving it on <body> drops a keyboard
      // user at the top of the page with no idea where they were.
      if (target && typeof target.focus === "function") target.focus();
    };
  }, [open, returnFocusTo]);

  // Escape + focus trap. A modal that lets Tab escape into the page behind it
  // is not a modal; screen-reader users end up operating hidden controls.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const focusable = Array.from(
        root.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, close]);

  if (!open) return null;

  const toggle = (key) => {
    // 'necessary' is deliberately unreachable here as well as disabled in the
    // markup — belt and braces, because a UI that can grant "necessary: false"
    // would write a category the store does not model.
    if (key === "necessary") return;
    setSelection((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = () => {
    setConsent({
      analytics: selection.analytics === true,
      marketing: selection.marketing === true,
      region,
    });
    close();
  };

  const handleWithdrawAll = () => {
    withdraw();
    // Reflect immediately: the user must SEE the withdrawal took effect rather
    // than trust it. The dialog stays open for that reason.
    setSelection({ analytics: false, marketing: false });
  };

  return (
    <div className="consent-prefs__overlay" role="presentation">
      <div
        className="consent-prefs"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-prefs-title"
        ref={dialogRef}
      >
        <h2 id="consent-prefs-title" className="consent-prefs__title">
          Cookie &amp; tracking preferences
        </h2>
        <p className="consent-prefs__intro">
          Choose what WTF Go may load. You can change or withdraw this at any time from the
          &ldquo;Cookie settings&rdquo; link in the footer.
        </p>

        <ul className="consent-prefs__list">
          {CATEGORY_COPY.map((cat, i) => {
            const isNecessary = cat.key === "necessary";
            const checked = isNecessary ? true : selection[cat.key] === true;
            return (
              <li className="consent-prefs__row" key={cat.key}>
                <div className="consent-prefs__text">
                  <span className="consent-prefs__catTitle">{cat.title}</span>
                  <span className="consent-prefs__catBody">{cat.body}</span>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={checked}
                  aria-label={cat.title}
                  /* aria-disabled, not only `disabled`: a plain disabled button
                     is skipped by screen readers entirely, so the user never
                     learns the category exists or why it cannot be changed. */
                  aria-disabled={isNecessary ? "true" : undefined}
                  disabled={isNecessary}
                  /* Initial focus goes to the first ENABLED control. Pointing it
                     at the disabled Necessary switch would silently focus
                     nothing and leave the keyboard user outside the modal. */
                  ref={isNecessary ? null : (el) => { if (i === 1) firstFocusRef.current = el; }}
                  className={`consent-prefs__switch${checked ? " is-on" : ""}`}
                  onClick={() => toggle(cat.key)}
                >
                  <span className="consent-prefs__knob" aria-hidden="true" />
                  <span className="consent-prefs__srOnly">{checked ? "on" : "off"}</span>
                </button>
              </li>
            );
          })}
        </ul>

        <div className="consent-prefs__actions">
          {/* §7: withdrawal must be as easy as granting — same dialog, one click,
              same visual weight as Save. */}
          <button
            type="button"
            className="consent-prefs__btn consent-prefs__btn--withdraw"
            onClick={handleWithdrawAll}
          >
            Withdraw all consent
          </button>
          <div className="consent-prefs__spacer" />
          <button type="button" className="consent-prefs__btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="consent-prefs__btn consent-prefs__btn--primary"
            onClick={handleSave}
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentPreferences;
